import { forwardRef, useEffect, useRef } from "react";
import type { ReactElement, ReactNode, Ref } from "react";
import { Spinner } from "../../atoms/Spinner";
import { cn } from "../../lib/cn";

export interface InfiniteScrollListProps<T> {
  /** Rows to render. */
  items: T[];
  /** Stable React key for a row. */
  keyOf: (item: T) => string;
  /** Row content — rendered inside a keyed <li>. */
  renderItem: (item: T) => ReactNode;
  /** More pages exist behind the sentinel. */
  hasMore: boolean;
  /** A page fetch is in flight — suppresses duplicate onLoadMore calls. */
  isLoading: boolean;
  /** Fetch the next page. */
  onLoadMore: () => void;
  /** Rendered when hasMore is false. Defaults to "No more items". */
  endMessage?: ReactNode;
  /** Replaces the list when there are no rows and nothing is loading. */
  emptyState?: ReactNode;
  className?: string;
}

/**
 * Observes a bottom sentinel <li> and calls onLoadMore when it scrolls into
 * view and another page is available. Re-arms when isLoading flips back to
 * false; disconnects on cleanup.
 */
function useInfiniteSentinel(
  hasMore: boolean,
  isLoading: boolean,
  onLoadMore: () => void,
) {
  const sentinelRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (isLoading) {
          return;
        }
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onLoadMore();
          }
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, onLoadMore]);

  return sentinelRef;
}

function InfiniteScrollListInner<T>(
  props: InfiniteScrollListProps<T>,
  ref: Ref<HTMLUListElement>,
) {
  const {
    items,
    keyOf,
    renderItem,
    hasMore,
    isLoading,
    onLoadMore,
    endMessage,
    emptyState,
    className,
  } = props;
  const sentinelRef = useInfiniteSentinel(hasMore, isLoading, onLoadMore);

  if (items.length === 0 && !isLoading) {
    if (!emptyState) {
      return null;
    }
    return <div className={className}>{emptyState}</div>;
  }

  return (
    <ul ref={ref} className={cn("w-full", className)}>
      {items.map((item) => (
        <li key={keyOf(item)}>{renderItem(item)}</li>
      ))}
      {isLoading ? (
        <li className="flex items-center justify-center py-4">
          <Spinner />
        </li>
      ) : null}
      {!hasMore && items.length > 0 ? (
        <li className="type-caption py-4 text-center text-content-secondary">
          {endMessage ?? "No more items"}
        </li>
      ) : null}
      {hasMore ? <li ref={sentinelRef} aria-hidden="true" className="h-px" /> : null}
    </ul>
  );
}

const InfiniteScrollListForwardRef = forwardRef<
  HTMLUListElement,
  InfiniteScrollListProps<unknown>
>(InfiniteScrollListInner);
InfiniteScrollListForwardRef.displayName = "InfiniteScrollList";

/**
 * Infinite-scrolling list — an IntersectionObserver sentinel fires onLoadMore
 * while pages remain; a Spinner row shows during loads.
 */
export const InfiniteScrollList = InfiniteScrollListForwardRef as <T>(
  props: InfiniteScrollListProps<T> & { ref?: Ref<HTMLUListElement> },
) => ReactElement;
